import { VideoPresets, VideoQuality as LkVideoQuality, type VideoCodec, type ScalabilityMode } from 'livekit-client'

// ─────────────────────────────────────────────────────────────────────
// PUBLISH side — controls what *I* send (uplink).
//
// Camera publishes H.264 SIMULCAST (3 layers) by default as of 2026-06-19.
// VP9 SVC (default 2026-05-05..06-19) is cheaper on the publisher (1 encode)
// and ~30% better per bitrate — BUT the decode cost lands on every subscriber,
// and the SFU does not transcode, so on a big call each client decodes ~16-20
// streams of whatever the publishers sent. The original note ("Chrome+Electron
// only so VP9 hardware support is fine") was wrong for macOS: NO Mac has
// hardware VP9 decode (not even Apple Silicon — Apple shipped HW AV1 on M3 but
// never VP9), so every Mac SOFTWARE-decodes VP9. On a 20-camera call that melts
// the CPU (~5 GB RAM, multi-second freezes → tiles "flicker") — the 2026-06-19
// incident. H.264 is hardware-decoded on all Macs (VideoToolbox) and virtually
// all Windows GPUs, so it's the right default for a mixed fleet: pay a little
// more uplink/encode (×1 per sender) to make decode cheap+HW everywhere (×20
// per viewer). Codec is per-PUBLISHER (SFU forwards as-is) so this only helps
// once senders are on H.264. Per-device revert/QA: localStorage
// 'voiceVideoCodec'='vp9'. Global revert: flip cameraVideoCodec()'s default.
// ─────────────────────────────────────────────────────────────────────

// Camera codec. H.264 default (HW-decoded everywhere incl. Mac); 'vp9' opt-in
// per device via localStorage for A/B or revert. See the block above.
export function cameraVideoCodec(): VideoCodec {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('voiceVideoCodec') === 'vp9') return 'vp9'
  } catch { /* noop */ }
  return 'h264'
}

// Decode-budget: surgical busy-call cap (2026-06-23). When ON, a busy call
// (bigRoomForcedLow) hard-pins every NON-hero tile to 180p via the per-tile
// useTileVideoDimensions hook — the hook is the layer authority for 'auto'
// users and was overriding the flat big-room setVideoQuality(LOW) on its next
// tick, so the coarse threshold cap barely helped 'auto' (the default). Only
// the hero tile (the focused / speaker-spotlight you're actually looking at)
// keeps its size-based high layer, so the call decodes 1 big + N×180p instead
// of N×540p/1080p.
//
// DEFAULT ON (flipped 2026-06-23 after landing the no-op flag first). Kill-
// switch: localStorage 'voiceDecodeBudget'='off' reverts one device to the old
// size-based-for-all behavior if a busy call looks wrong (hero soft, or a tile
// not capped). Confirm the drop in voice_diag inbound-rtp freezeCount /
// fps-at-0 on the next big call.
export function decodeBudget(): boolean {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('voiceDecodeBudget') === 'off') return false
  } catch { /* noop */ }
  return true
}

export type VideoQuality = 'auto' | 'low' | 'hd' | 'fhd'

export interface VideoConfig {
  // Capture-side constraint passed to getUserMedia via LiveKit's
  // VideoCaptureOptions. "Ideal" semantics — camera falls back to its
  // closest supported mode if it can't hit this resolution.
  resolution: { width: number; height: number; frameRate?: number }
  // Top-layer encoding. With VP9 SVC the SFU can deliver lower spatial
  // layers from the same stream; this bitrate is the ceiling for the
  // full 1080p layer.
  encoding: { maxBitrate: number; maxFramerate: number }
  // Codec for the publish. VP9 across the board — H.264 only as
  // backup is left to LiveKit's defaults.
  codec: VideoCodec
  // SVC scalability mode. 'L3T3_KEY' = 3 spatial × 3 temporal layers
  // with key-frames per spatial layer (the LiveKit-recommended default
  // for VP9). For low (single-layer) we use 'L1T3' — temporal scaling
  // only, since 180p has nothing meaningful to scale further.
  scalabilityMode: ScalabilityMode
}

export function getVideoConfig(quality: VideoQuality): VideoConfig {
  switch (quality) {
    case 'low':
      // Bandwidth-saver: 180p single spatial layer at 250 kbps. 'L1T3'
      // gives the SFU temporal-layer drops if the subscriber's downlink
      // gets even tighter, but spatial-wise it's just 180p.
      return {
        resolution: { ...VideoPresets.h180.resolution, frameRate: 24 },
        encoding: { maxBitrate: 250_000, maxFramerate: 24 },
        codec: cameraVideoCodec(),
        scalabilityMode: 'L1T3',
      }
    case 'hd':
      // 720p VP9 SVC, ~4 Mbps top — bumped from the previous 2.5 Mbps
      // simulcast top because VP9 is more efficient and the user's
      // intent here is "quality first". Subscribers on weak downlinks
      // get the L1 (180p) or L2 (~360p) spatial layer from the SVC.
      return {
        resolution: VideoPresets.h720.resolution,
        encoding: { maxBitrate: 4_000_000, maxFramerate: 30 },
        codec: cameraVideoCodec(),
        scalabilityMode: 'L3T3_KEY',
      }
    case 'fhd':
      // Explicit "max quality" opt-in: 1080p VP9 SVC at 8 Mbps. NOT the
      // default anymore — only for users who know they have the uplink.
      // See 'auto' below for why the default was lowered.
      return {
        resolution: VideoPresets.h1080.resolution,
        encoding: { maxBitrate: 8_000_000, maxFramerate: 30 },
        codec: cameraVideoCodec(),
        scalabilityMode: 'L3T3_KEY',
      }
    case 'auto':
    default:
      // DEFAULT — audio-first. 720p VP9 SVC capped at 1.2 Mbps.
      //
      // Was 1080p @ 8 Mbps. On a modest uplink (~550 kbps measured in a
      // prod call) the encoder probes toward the 8 Mbps ceiling, overshoots
      // the real link, triggers loss-based BWE collapse to the 5 kbps floor,
      // and STARVES AUDIO — Opus needs ~40 kbps and gets nothing. Repro:
      // Sergey QA "очень плохо слышно" with camera on, fine the instant he
      // turned it off. A 1.2 Mbps ceiling lets VP9 SVC + GCC fit the sent
      // bitrate to the real link instead of crushing it; subscribers on weak
      // downlinks still pull the L1/L2 (180p/360p) layers from the SVC
      // stream. We trade some video sharpness for reliable audio — the whole
      // point of the call. Users who want max can pick 'fhd' (1080p@8M).
      return {
        resolution: VideoPresets.h720.resolution,
        encoding: { maxBitrate: 1_200_000, maxFramerate: 30 },
        codec: cameraVideoCodec(),
        scalabilityMode: 'L3T3_KEY',
      }
  }
}

export function readVideoQuality(): VideoQuality {
  if (typeof localStorage === 'undefined') return 'auto'
  const v = localStorage.getItem('videoQuality') as VideoQuality | null
  return v === 'low' || v === 'hd' || v === 'fhd' || v === 'auto' ? v : 'auto'
}

// ─────────────────────────────────────────────────────────────────────
// SUBSCRIBE side — controls what *I* receive (downlink cap).
//
// LiveKit's `RemoteTrackPublication.setVideoQuality(q)` puts a *ceiling*
// on the layer the SFU is allowed to forward. Setting HIGH is the
// natural default ("auto"): the SFU will give us the top layer when our
// bandwidth allows and step down on its own under congestion. MEDIUM /
// LOW let a user with a weak downlink (e.g. 1.5 Mbps total) opt out of
// receiving 1080p streams at all so they can keep up with a multi-peer
// call without the BWE thrashing.
// ─────────────────────────────────────────────────────────────────────

export type IncomingQuality = 'auto' | 'medium' | 'low'

export function getIncomingLkQuality(q: IncomingQuality): LkVideoQuality {
  switch (q) {
    case 'low':
      return LkVideoQuality.LOW
    case 'medium':
      return LkVideoQuality.MEDIUM
    case 'auto':
    default:
      return LkVideoQuality.HIGH
  }
}

export function readIncomingQuality(): IncomingQuality {
  if (typeof localStorage === 'undefined') return 'auto'
  const v = localStorage.getItem('incomingVideoQuality') as IncomingQuality | null
  return v === 'low' || v === 'medium' || v === 'auto' ? v : 'auto'
}

// ─────────────────────────────────────────────────────────────────────
// Busy-room auto cap: once enough cameras are live, force every incoming
// camera to the lowest (180p) layer. The DECODE bill — not bandwidth — is
// the binding constraint: HW video decoders cap the number of concurrent
// sessions AND total megapixels/s, so a handful of 540p/1080p tiles spills
// to software decode and melts the CPU (multi-second freezes → "cameras
// flicker"). 180p×N is cheap and HW-decoded everywhere.
//
// Threshold lowered 20→6 on 2026-06-23 from prod telemetry (voice_diag,
// channel cc31d781 team call): the freezes hit at a PEAK of 11 cameras
// (avg 7.5), so the old ENGAGE=20 NEVER fired — ~30% of received video
// stayed ≥540p and ~20% of inbound frames were frozen, on BOTH Mac and
// Windows. `applyBigRoomCap` counts `publishers` = self + every remote
// camera, so ENGAGE=6 forces LOW once there are 7+ cameras (≈6+ remote
// tiles to decode) — exactly the zone that melted. The deadband
// (RELEASE=4) stops a join/leave at the edge from flapping quality.
//
// NOTE: this coarse cap also drops the focused/pinned/speaker tile to 180p.
// A follow-up "decode budget" change keeps that one hero tile sharp (via
// per-tile useTileVideoDimensions) while the grid stays at 180p; until then
// the trade is reliable video over a sharp spotlight on a 7+ camera call.
// ─────────────────────────────────────────────────────────────────────

export const BIG_ROOM_VIDEO_ENGAGE = 6 // publishers > this → force LOW (7+ cameras)
export const BIG_ROOM_VIDEO_RELEASE = 4 // publishers <= this → release

export function nextBigRoomLatch(cameraPublishers: number, engaged: boolean): boolean {
  if (cameraPublishers > BIG_ROOM_VIDEO_ENGAGE) return true
  if (cameraPublishers <= BIG_ROOM_VIDEO_RELEASE) return false
  return engaged
}

// ─────────────────────────────────────────────────────────────────────
// PUBLISHER-side busy-room cap for SINGLE-LAYER canvas tracks (2026-06-26).
//
// A processed camera (virtual background / blur / zoom / face-center) is
// published as ONE flat canvas layer — `simulcast: false`, `scalabilityMode:
// undefined` — because a canvas captureStream can't simulcast without the
// encodings mismatch that freezes video for peers (mediaManager
// publishCameraWithEffect, invariant #68). With only one spatial layer the
// SFU has nothing lower to forward, so the subscribe-side busy-room 180p cap
// (useTileVideoDimensions + bigRoomForcedLow) is a no-op for these
// publishers — every viewer pulls the full preset bitrate (auto = 1.2 Mbps).
// On a busy call the PUBLISHER must therefore shrink its own sender: drop the
// encoding to ~180p / 250 kbps via RTCRtpSender.setParameters (non-disruptive
// — no republish, no SDP renegotiation, so none of the republish-storm risks
// in invariants #37/#41). Plain-camera (simulcast / VP9-SVC) publishers are
// left untouched: their layers let the subscribe-side cap give each viewer
// the right one, and the caller only invokes this for a processed publication
// (`combinedProcessor != null`).
export function busyRoomPublishSenderParams(opts: {
  bigRoomForcedLow: boolean
  preset: VideoConfig
}): { maxBitrate: number; scaleResolutionDownBy: number } {
  if (!opts.bigRoomForcedLow) {
    // Released → restore the preset's full bitrate at native resolution.
    return { maxBitrate: opts.preset.encoding.maxBitrate, scaleResolutionDownBy: 1 }
  }
  const height = opts.preset.resolution.height || 720
  // Snap down toward ~180p (e.g. 720→4×, 1080→6×, 180→1×). The encoder needs
  // an integer factor; never below 1.
  const scaleResolutionDownBy = Math.max(1, Math.round(height / 180))
  return {
    maxBitrate: Math.min(250_000, opts.preset.encoding.maxBitrate),
    scaleResolutionDownBy,
  }
}
